import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { kycRoute } from '../lib/kyc';
import { BulkPayout } from '../components/BulkPayout';
import { 
  Send, 
  Wallet, 
  UserCheck, 
  ArrowRight, 
  Loader2,
  AlertCircle,
  CheckCircle,
  IndianRupee,
  Building2,
  Users,
  FileSpreadsheet
} from 'lucide-react';

export default function CreatePayoutPage() {
  const { api, merchant } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [mode, setMode] = useState('single');
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [walletSummary, setWalletSummary] = useState(null);
  const [selectedBeneficiary, setSelectedBeneficiary] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    beneficiary_id: '',
    amount: '',
    merchant_reference: '',
    remarks: ''
  });
  
  // Fee calculation
  const [feeDetails, setFeeDetails] = useState({
    amount: 0,
    fee: 10,
    gst: 1.8,
    total: 0
  });

  const fetchData = useCallback(async () => {
    try {
      const [beneficiariesRes, walletRes] = await Promise.all([
        api.get('/beneficiaries/'),
        api.get('/wallet/summary')
      ]);
      setBeneficiaries(beneficiariesRes.data || []);
      setWalletSummary(walletRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    // Fetch the authoritative fee breakdown from the backend (display only)
    const amount = parseFloat(formData.amount) || 0;
    if (amount <= 0) {
      setFeeDetails({ amount: 0, fee: 0, gst: 0, total: 0 });
      return;
    }
    const t = setTimeout(async () => {
      try {
        const res = await api.get(`/merchant/fees/preview?amount=${amount}`);
        const d = res.data;
        setFeeDetails({
          amount,
          fee: d.platform_fee,
          gst: d.tax_amount,
          total: d.total_debit,
        });
      } catch {
        setFeeDetails({ amount, fee: 0, gst: 0, total: amount });
      }
    }, 350);
    return () => clearTimeout(t);
  }, [formData.amount, api]);

  const handleBeneficiarySelect = (beneficiaryId) => {
    const beneficiary = beneficiaries.find(b => b.id === beneficiaryId);
    setSelectedBeneficiary(beneficiary);
    setFormData(prev => ({ ...prev, beneficiary_id: beneficiaryId }));
  };

  const validateForm = () => {
    if (!formData.beneficiary_id) {
      toast.error('Please select a beneficiary');
      return false;
    }
    
    const amount = parseFloat(formData.amount);
    if (!amount || amount < 1000) {
      toast.error('Minimum payout amount is ₹1,000');
      return false;
    }
    if (amount > 100000) {
      toast.error('Maximum payout amount is ₹1,00,000');
      return false;
    }
    
    if (feeDetails.total > walletSummary?.balance?.available) {
      toast.error('Insufficient wallet balance');
      return false;
    }
    
    return true;
  };

  const handleProceed = () => {
    if (validateForm()) {
      setShowConfirmation(true);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    
    try {
      const response = await api.post('/payouts/', {
        beneficiary_id: formData.beneficiary_id,
        amount: parseFloat(formData.amount),
        merchant_reference: formData.merchant_reference || null,
        remarks: formData.remarks || null
      });
      
      toast.success(`Payout ${response.data.order_id} created successfully!`);
      navigate('/dashboard/payouts');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to create payout';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const payoutReady = merchant && merchant.kyc_status === 'approved' && merchant.services?.payout_enabled && merchant.account_status !== 'suspended' && !merchant.is_payout_frozen;
  if (merchant && !payoutReady) {
    const needsKyc = merchant.kyc_status !== 'approved';
    return (
      <div className="max-w-2xl mx-auto" data-testid="payout-locked-state">
        <Card className="border-amber-500/30">
          <CardContent className="py-12 text-center space-y-4">
            <div className="w-14 h-14 rounded-full bg-amber-500/15 flex items-center justify-center mx-auto">
              <AlertCircle className="w-7 h-7 text-amber-500" />
            </div>
            <h1 className="text-2xl font-heading font-bold">Payout Not Activated</h1>
            <p className="text-muted-foreground max-w-md mx-auto">
              {needsKyc
                ? 'Your KYC verification must be completed and approved before payout services can be activated.'
                : merchant.account_status === 'suspended'
                  ? 'Your account is currently suspended. Please contact support.'
                  : merchant.is_payout_frozen
                    ? 'Your payouts are temporarily frozen. Please contact support.'
                    : 'Payout service is not yet enabled for your account. Our team will activate it shortly.'}
            </p>
            {needsKyc ? (
              <Button onClick={() => navigate(kycRoute(merchant?.kyc_status))} data-testid="complete-kyc-btn">
                Complete KYC <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            ) : (
              <Button variant="outline" onClick={() => navigate('/dashboard')} data-testid="view-kyc-status-btn">
                View KYC Status
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (showConfirmation) {
    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-heading font-bold">Confirm Payout</h1>
          <p className="text-muted-foreground">Review the details before submitting</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-primary" />
              Payout Summary
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Beneficiary Details */}
            <div className="p-4 rounded-lg bg-muted/50 border">
              <p className="text-sm text-muted-foreground mb-2">Beneficiary</p>
              <p className="font-semibold">{selectedBeneficiary?.beneficiary_name}</p>
              <p className="text-sm text-muted-foreground">
                {selectedBeneficiary?.bank_name} • {selectedBeneficiary?.account_number_masked}
              </p>
              <p className="text-sm text-muted-foreground">IFSC: {selectedBeneficiary?.ifsc_code}</p>
            </div>

            {/* Amount Breakdown */}
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Payout Amount</span>
                <span className="font-medium">{formatCurrency(feeDetails.amount)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Platform Fee</span>
                <span>{formatCurrency(feeDetails.fee)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Tax</span>
                <span>{formatCurrency(feeDetails.gst)}</span>
              </div>
              <div className="border-t pt-3 flex justify-between font-semibold">
                <span>Total Debit</span>
                <span className="text-primary">{formatCurrency(feeDetails.total)}</span>
              </div>
            </div>

            {/* Wallet Info */}
            <div className="p-4 rounded-lg bg-primary/5 border border-primary/20">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm text-muted-foreground">Available Balance</p>
                  <p className="text-lg font-semibold">{formatCurrency(walletSummary?.balance?.available || 0)}</p>
                </div>
                <ArrowRight className="w-5 h-5 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">After Payout</p>
                  <p className="text-lg font-semibold">
                    {formatCurrency((walletSummary?.balance?.available || 0) - feeDetails.total)}
                  </p>
                </div>
              </div>
            </div>

            {formData.merchant_reference && (
              <div className="text-sm">
                <span className="text-muted-foreground">Reference: </span>
                <span>{formData.merchant_reference}</span>
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <Button 
                variant="outline" 
                onClick={() => setShowConfirmation(false)}
                className="flex-1"
              >
                Back to Edit
              </Button>
              <Button 
                onClick={handleSubmit}
                disabled={submitting}
                className="flex-1"
                data-testid="confirm-payout-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Send className="mr-2 h-4 w-4" />
                    Confirm Payout
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-heading font-bold">Create Payout</h1>
        <p className="text-muted-foreground">Send money to your beneficiaries via bank transfer</p>
      </div>

      {/* Mode selector */}
      <div className="inline-flex rounded-lg border bg-muted/30 p-1" data-testid="payout-mode-tabs">
        <button
          onClick={() => setMode('single')}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${mode === 'single' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
          data-testid="mode-single-btn"
        >
          <Users className="w-4 h-4" />Single Payout
        </button>
        <button
          onClick={() => setMode('bulk')}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${mode === 'bulk' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
          data-testid="mode-bulk-btn"
        >
          <FileSpreadsheet className="w-4 h-4" />Bulk Payout
        </button>
      </div>

      {mode === 'bulk' ? (
        <BulkPayout api={api} />
      ) : (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Payout Form */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserCheck className="w-5 h-5" />
                Select Beneficiary
              </CardTitle>
              <CardDescription>Choose from your saved bank accounts</CardDescription>
            </CardHeader>
            <CardContent>
              {beneficiaries.length === 0 ? (
                <div className="text-center py-8">
                  <UserCheck className="w-12 h-12 mx-auto text-muted-foreground opacity-50 mb-4" />
                  <p className="text-muted-foreground mb-4">No beneficiaries added yet</p>
                  <Button onClick={() => navigate('/dashboard/beneficiaries')}>
                    Add Beneficiary
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {beneficiaries.map((beneficiary) => (
                    <div
                      key={beneficiary.id}
                      className={`p-4 rounded-lg border cursor-pointer transition-all ${
                        formData.beneficiary_id === beneficiary.id
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary/50'
                      }`}
                      onClick={() => handleBeneficiarySelect(beneficiary.id)}
                      data-testid={`beneficiary-${beneficiary.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium">{beneficiary.beneficiary_name}</p>
                          <p className="text-sm text-muted-foreground">
                            {beneficiary.bank_name} • {beneficiary.account_number_masked}
                          </p>
                          <p className="text-xs text-muted-foreground">IFSC: {beneficiary.ifsc_code}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {beneficiary.nickname && (
                            <Badge variant="secondary">{beneficiary.nickname}</Badge>
                          )}
                          {formData.beneficiary_id === beneficiary.id && (
                            <CheckCircle className="w-5 h-5 text-primary" />
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <IndianRupee className="w-5 h-5" />
                Payout Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="amount">Amount (INR)</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">₹</span>
                  <Input
                    id="amount"
                    type="number"
                    placeholder="25,000"
                    value={formData.amount}
                    onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                    className="pl-8 text-lg h-12"
                    min="1000"
                    max="100000"
                    data-testid="payout-amount-input"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Min: ₹1,000 • Max: ₹1,00,000
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="reference">Reference (Optional)</Label>
                <Input
                  id="reference"
                  placeholder="e.g., INV-2026-001"
                  value={formData.merchant_reference}
                  onChange={(e) => setFormData(prev => ({ ...prev, merchant_reference: e.target.value }))}
                  data-testid="payout-reference-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="remarks">Remarks (Optional)</Label>
                <Textarea
                  id="remarks"
                  placeholder="Payment notes..."
                  value={formData.remarks}
                  onChange={(e) => setFormData(prev => ({ ...prev, remarks: e.target.value }))}
                  rows={2}
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Summary Sidebar */}
        <div className="space-y-6">
          {/* Wallet Balance Card */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <Wallet className="w-4 h-4" />
                Wallet Balance
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-primary">
                {formatCurrency(walletSummary?.balance?.available || 0)}
              </p>
              <p className="text-xs text-muted-foreground mt-1">Available for payout</p>
              
              {walletSummary?.balance?.pending > 0 && (
                <p className="text-xs text-yellow-600 mt-2">
                  ₹{walletSummary.balance.pending.toLocaleString('en-IN')} held in pending
                </p>
              )}
            </CardContent>
          </Card>

          {/* Fee Breakdown Card */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Fee Breakdown</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Payout Amount</span>
                <span>{formatCurrency(feeDetails.amount)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Platform Fee</span>
                <span>{formatCurrency(feeDetails.fee)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Tax</span>
                <span>{formatCurrency(feeDetails.gst)}</span>
              </div>
              <div className="border-t pt-3 flex justify-between font-semibold">
                <span>Total Debit</span>
                <span className="text-primary">{formatCurrency(feeDetails.total)}</span>
              </div>
            </CardContent>
          </Card>

          {/* Limits Card */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Daily Limits</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Today's Used</span>
                <span>{formatCurrency(walletSummary?.limits?.daily_used || 0)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Remaining</span>
                <span className="text-green-600">{formatCurrency(walletSummary?.limits?.daily_remaining || 0)}</span>
              </div>
              <div className="w-full bg-muted rounded-full h-2 mt-2">
                <div 
                  className="bg-primary rounded-full h-2" 
                  style={{ 
                    width: `${Math.min(100, ((walletSummary?.limits?.daily_used || 0) / (walletSummary?.limits?.daily_limit || 500000)) * 100)}%` 
                  }}
                />
              </div>
            </CardContent>
          </Card>

          {/* Submit Button */}
          <Button 
            className="w-full h-12" 
            onClick={handleProceed}
            disabled={!formData.beneficiary_id || !formData.amount}
            data-testid="proceed-payout-btn"
          >
            <ArrowRight className="mr-2 h-4 w-4" />
            Proceed to Confirm
          </Button>
        </div>
      </div>
      )}
    </div>
  );
}
